import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
  input,
} from '@angular/core';
import { GalleryDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { firstValueFrom, forkJoin, of, take, tap } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { SectionService } from '../../../services/section.service';
import { StickyComponent } from '../../../shared-extra/components/sticky/sticky.component';
import { StickyContainerDirective } from '../../../shared-extra/directives/sticky-container.directive';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { DeleteAssetSectionDialogComponent } from '../../dialogs/delete-asset-section-dialog/delete-asset-section-dialog.component';
import { DeleteSectionDialogComponent } from '../../dialogs/delete-section-dialog/delete-section-dialog.component';
import { adaptGalleryWidth } from '../../lib/get-gallery-width';
import { AddSectionComponent } from '../add-section/add-section.component';
import { EditSectionTextComponent } from '../edit-section-text/edit-section-text.component';

@Component({
  selector: 'app-section-container',
  templateUrl: './section-container.component.html',
  styleUrls: ['./section-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InferredHoverDirective,
    StickyContainerDirective,
    StickyComponent,
    NgTemplateOutlet,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    AddSectionComponent,
    EditSectionTextComponent,
  ],
})
export class SectionContainerComponent {
  private check = inject(ChangeDetectorRef);
  private sectionService = inject(SectionService);
  private assetService = inject(AssetService);
  private dialog = inject(DialogService);
  @Input() gallery: GalleryDto;
  readonly section = input<Section>(undefined);
  readonly theme = input.required<GalleryTheme>();
  @Input() outsideActions: boolean;
  @Input() insideActions: boolean;
  @Input() darkBackground: boolean;
  @Input() sections: Section[];
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly noMaxWidth = input<boolean>();
  @Input() index: number;
  @Input() sectionContentInnerPosition: 'static' | 'relative' = 'static';
  @Output() onMoved = new EventEmitter<void>();
  @Output() addSection: EventEmitter<number> = new EventEmitter();
  @ViewChild('sectionContentInner') sectionContentInner: ElementRef<HTMLDivElement>;

  @HostListener('window:resize', ['$event']) winResize() {
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';
  }

  isDesktop = ScreenSizes.screenSize() === 'desktop';
  targetSpace = 100;
  hover = false;
  inferredHover = false;

  maxWidth = computed(() => {
    const noMaxWidth = this.noMaxWidth();
    const section = this.section();
    const theme = this.theme();
    if (noMaxWidth) return;
    if (!section || !theme) return;
    return adaptGalleryWidth(section.width ?? theme.section.width);
  });

  moveUp() {
    if (!this.canMoveUp()) return of(undefined);
    this.move(-1).subscribe();
  }

  moveDown() {
    if (!this.canMoveDown()) return of(undefined);
    this.move(1).subscribe();
  }

  canMoveUp() {
    return this.sectionIndex > 0;
  }

  canMoveDown() {
    return this.sectionIndex < this.sections.length - 1;
  }

  private get sectionIndex() {
    return this.sections.findIndex(s => s.id === this.section().id);
  }

  move(change: 1 | -1) {
    // Figure out what section we will be swapping positions with
    const sectionIndex = this.sections.findIndex(s => s.id === this.section().id);
    const newSectionIndex = sectionIndex + change;

    // Getting a reference to that "other" section
    const otherSection = this.sections[newSectionIndex];

    // Swapping the position between the two
    const oldPosition = this.section().position;
    const newPosition = otherSection.position;
    const section = this.section();
    section.position = newPosition;
    otherSection.position = oldPosition;

    this.sections.sort((a, b) => (a.position > b.position ? 1 : -1));

    // Updating both sections through the api
    return forkJoin([
      firstValueFrom(this.sectionService.updateSection(otherSection, { position: otherSection.position })),
      firstValueFrom(this.sectionService.updateSection(section, { position: section.position })),
    ]).pipe(tap(() => this.check.detectChanges()));
  }

  deleteSection() {
    const section = this.section();
    if (section.object === 'asset') {
      this.assetService
        .getAssetCount(this.gallery.id, section.id)
        .pipe(take(1))
        .subscribe(assetCount => {
          if (assetCount > 0) {
            this.dialog.open(DeleteAssetSectionDialogComponent, { section: this.section(), assetCount });
          } else {
            this.sectionService.deleteSection(this.section()).subscribe();
          }
        });
    } else {
      this.dialog.open(DeleteSectionDialogComponent, { section: section });
    }
  }

  getPaddingY(container: HTMLElement, sticky: HTMLDivElement) {
    return () => {
      const containerHeight = container.getBoundingClientRect().height;
      const stickyHeight = sticky.getBoundingClientRect().height;
      const spaceLeft = containerHeight - stickyHeight;
      if (spaceLeft > this.targetSpace * 2) return this.targetSpace;
      return Math.floor(spaceLeft / 2);
    };
  }
}
